export const mockPortfolioData = {
  personal: {
    name: "Dwayne Webb",
    title: "Senior Software Engineer",
    subtitle: "Frontend-Heavy Full-Stack Engineer, Building User-Centric Products",
    tagline:
      "Frontend-focused Engineer with 10 years building production systems. Evolved from enterprise platforms to frontend-heavy full-stack development, specializing in React ecosystems, real-time interfaces, and performance optimization. Passionate about crafting exceptional user experiences that drive business impact.",
    phone: "+1 (206) 489-0626",
    email: "dwayne1992x@gmail.com",
    location: "Spokane Valley, WA",
    resumeUrl: "https://drive.google.com/file/d/1G4LBpVmerxGk9mVz7_yGSxM0oDCF62wt/view?usp=sharing",
    calendlyUrl: "https://calendly.com/dwayne1992x/30min",
    github: "https://github.com/dwayneex",
    portfolio: "#" // "*"
  },

  about: {
    summary:
      "Frontend-focused Engineer specializing in React ecosystems and modern web technologies. Evolved from enterprise development to frontend-heavy full-stack engineering, with deep expertise in performance optimization, real-time systems, and user interface design. Currently leveraging AI tools to accelerate development while maintaining exceptional code quality.",
    experience:
      "10 years • Enterprise platforms → Design consultancies → Frontend-focused logistics/TMS • AI-accelerated development",
    expertise:
      "React/Next.js • TypeScript • Performance Optimization • WebSockets • D3.js/Data Visualization • Modern CSS/TailwindCSS • AI-assisted Development",
    mindset:
      "Frontend excellence drives user adoption. Optimize for performance, accessibility, and developer experience.",
    metrics: [
      "Real-time dashboard latency: 8s → 200ms",
      "Frontend performance optimizations: +30% improvement", 
      "CI/CD pipeline efficiency: +35% faster deployments",
      "AI-accelerated development: 10K+ LOC refactor in 2 days",
      "Cross-functional leadership: Design → Engineering → Product"
    ]
  },

  experience: [
    {
      id: "1",
      company: "Shipwell",
      position: "Senior Software Engineer",
      duration: "10/2020 - Present",
      description:
        "Series B Supply Chain & Logistics Startup",
      achievements: [
        "Architected real-time tracking dashboards with React/Redux/WebSockets, reducing latency from 8s to 200ms",
        "Delivered 30% performance improvements through React optimization: lazy loading, code splitting, async rendering",
        "Built component library with Storybook + TailwindCSS, standardizing design system across product teams",
        "Created interactive data visualizations with D3.js and Plotly.js for complex logistics workflows",
        "Streamlined frontend CI/CD pipeline, achieving 35% faster deployments with GitHub Actions",
        "Led cross-functional frontend initiatives, bridging design, product, and engineering requirements",
        "Executed 10K+ LOC frontend refactor in 2 days using AI-assisted development (Cursor/Windsurf)"
      ],
      order: 1,
      createdAt: "2025-01-01T00:00:00.000Z"
    },
    {
      id: "2",
      company: "Work & Co",
      position: "Member Of Technical Staff",
      duration: "11/2016 - 09/2020",
      description:
        "Design-led product consultancy (acquired by Accenture Song in Jan 2024)",
      achievements: [
        "Built production MVPs for 20+ startups across wellness, retail, fintech, and sports tech verticals",
        "Developed real-time frontend systems with GraphQL/WebSockets, optimizing for performance and UX",
        "Created reusable design systems with Storybook/TailwindCSS, accelerating cross-team development",
        "Specialized in mobile-first, responsive React applications with focus on conversion optimization",
        "Collaborated directly with designers and clients to translate brand requirements into functional UIs"
      ],
      order: 2,
      createdAt: "2025-01-01T00:00:00.000Z"
    },
    {
      id: "3",
      company: "Atlassian",
      position: "Web Developer",
      duration: "01/2015 - 11/2016",
      description:
        "Jira Cloud Ecosystem",
      achievements: [
        "Built React/D3.js analytics dashboards, improving Jira reporting capabilities by 30%",
        "Developed 5 production Jira Cloud plugins using Atlassian Connect + React architecture",
        "Optimized frontend performance for large enterprise datasets, reducing load times significantly",
        "Created interactive reporting interfaces enabling real-time data manipulation and visualization",
        "Partnered with Agile teams to deliver responsive, enterprise-grade frontend solutions"
      ],
      order: 3,
      createdAt: "2025-01-01T00:00:00.000Z"
    }
  ],

  projects: [
    {
      id: "1",
      title: "Shipwell - Real-Time Logistics Platform",
      description:
        "Comprehensive Transportation Management System (TMS) powering end-to-end supply chain operations. Architected real-time order tracking dashboards providing instant visibility into shipments across multiple carriers. Built customer-facing web portals enabling shippers to manage orders, track deliveries, and access documentation seamlessly. Developed internal operations dashboards for logistics teams to monitor exceptions, optimize routes, and streamline workflows. Integrated Swifty AI agent leveraging RAG and LLMs to automate manual logistics processes including order creation, status updates, document management, and dock scheduling—significantly reducing operational overhead. Created internal engineering tooling for proactive issue detection and on-call mitigation, improving system reliability and reducing MTTR by 40%.",
      impact: "8s → 200ms latency • 18% cost reduction • 40% MTTR reduction • AI automation reducing manual work by 60%",
      tech: ["React", "Redux", "Python", "Node.js", "Redis", "WebSockets", "Kafka", "RAG", "LLMs", "D3.js", "TypeScript"],
      productUrl: "https://www.shipwell.com/ai-in-supply-chain",
      screenshot: "/images/projects/shipwell-ai.png",
      order: 1,
      featured: true,
      createdAt: "2025-01-01T00:00:00.000Z"
    },
    {
      id: "2",
      title: "3D Building Design System",
      description:
        "Interactive 3D building design platform enabling architects, engineers, and manufacturers to visualize, customize, and engineer cold-formed steel buildings in real-time. The system provides comprehensive design tools with instant rendering, structural calculations, and material optimization. Users can explore different building configurations, see realistic 3D previews, generate engineering drawings, and create quotes—all from a unified interface. The platform transforms the traditional building design process by making complex architectural decisions accessible and intuitive, reducing design cycles and improving client presentations.",
      impact: "Faster design cycles • Improved client engagement • Reduced engineering errors • Streamlined manufacturing workflows",
      tech: ["React", "Three.js", "WebGL", "TypeScript", "C#", ".NET", "AWS", "Pulumi", "Minikube", "PostgreSQL", "3D Rendering"],
      productUrl: "https://designer.actbuildingsystems.com/",
      screenshot: "/images/projects/act-designer-3d.png",
      order: 2,
      featured: true,
      createdAt: "2025-01-01T00:00:00.000Z"
    },
    {
      id: "3",
      title: "Hospitality & Restaurant Platform",
      description:
        "Complete digital guest engagement suite for restaurants combining online ordering, loyalty programs, and customer relationship management. Built merchant portals enabling restaurant owners to configure ordering flows, manage menu items, set up promotional campaigns, and track customer analytics. Developed sophisticated loyalty program features with points, rewards, and personalized offers. Created seamless online ordering experiences with custom branding, flexible payment options, and order tracking. The platform integrates multiple touchpoints to create cohesive customer journeys, driving repeat visits and increasing average order values through intelligent engagement strategies.",
      impact: "Increased order conversion • Higher customer retention • Improved average order value • Streamlined restaurant operations",
      tech: ["Nuxt.js", "PHP", "Kohana", "Vanilla JavaScript", "Payment Processing", "Loyalty APIs", "Analytics", "REST APIs"],
      productUrl: "https://lionschoice.orderexperience.net/5f870f5995b70187558b4577/menu",
      screenshot: "/images/projects/paytronix-menu.png",
      order: 3,
      featured: true,
      createdAt: "2025-01-01T00:00:00.000Z"
    },
    {
      id: "4",
      title: "Healthcare Teletherapy Platform",
      description:
        "Advanced teletherapy platform connecting healthcare providers with patients for virtual therapy sessions. Streamlined provider onboarding workflows to reduce administrative burden and accelerate time-to-service. Enhanced the Kanga therapy platform with improved scheduling tools, session management, and documentation capabilities. Built features for secure video conferencing, session recording, progress tracking, and compliance reporting. The platform enables healthcare organizations to expand access to therapy services, reduce geographic barriers, and serve more patients while maintaining high-quality care standards.",
      impact: "Expanded access to therapy • Reduced geographic barriers • Improved provider efficiency • Enhanced patient outcomes",
      tech: ["Angular 18", "Django", "Go", "FastAPI", "AWS", "Airflow", "WebRTC", "Video Conferencing", "Healthcare APIs", "HIPAA Compliance"],
      productUrl: "https://presence.com/kanga-online-therapy-platform/",
      screenshot: "/images/projects/presence-kanga.png",
      order: 4,
      featured: true,
      createdAt: "2025-01-01T00:00:00.000Z"
    },
    {
      id: "5",
      title: "Clinical Documentation & Audit Platform",
      description:
        "AI-powered Clinical Documentation Improvement (CDI) and audit platform designed to reduce clinical coding errors and improve mid-cycle revenue systems. The Retrospect project enables healthcare organizations to perform retrospective audits of medical records, identify documentation gaps, and ensure coding accuracy. Built sophisticated analytics dashboards providing insights into documentation quality, compliance metrics, and revenue impact. The platform uses advanced algorithms to analyze clinical notes, suggest improvements, and track improvements over time—helping hospitals optimize their revenue cycle while maintaining regulatory compliance.",
      impact: "Reduced coding errors • Improved mid-cycle revenue • Enhanced documentation quality • Better compliance rates",
      tech: ["React", "Vue", "Express", "Nest.js", "PostgreSQL", "Redis", "AI/ML", "Healthcare APIs", "Data Analytics", "Clinical Data Processing"],
      productUrl: "https://iodinesoftware.com/solutions/pre-bill/",
      screenshot: "/images/projects/iodine-prebill.png",
      order: 5,
      featured: true,
      createdAt: "2025-01-01T00:00:00.000Z"
    },
    {
      id: "6",
      title: "Insurance Quoting Platform",
      description:
        "Modern insurance quoting workflow platform streamlining the policy application and quote generation process. Built version 2 of the quoting system integrating with Canopy Connect APIs for seamless data exchange and Microsoft Dynamics for CRM functionality. Developed intuitive form interfaces using Survey.js for dynamic question flows based on customer responses. Created agent-facing dashboards for quote management, policy comparison, and customer communication. The platform enables insurance agents to generate accurate quotes faster, compare multiple carriers, and provide customers with transparent pricing information—significantly improving the overall insurance shopping experience.",
      impact: "Faster quote generation • Improved quote accuracy • Enhanced agent productivity • Better customer experience",
      tech: ["Next.js", "Survey.js", "Canopy Connect", "Microsoft Dynamics", "CRM Integration", "TypeScript", "React"],
      productUrl: "https://www.brightway.com/",
      screenshot: "/images/projects/brightway-quote.png",
      order: 6,
      featured: true,
      createdAt: "2025-01-01T00:00:00.000Z"
    },
    {
      id: "7",
      title: "Childcare Provider Search Platform",
      description:
        "Government-compliant childcare provider search and discovery platform for the Department of Human Services. Built comprehensive search functionality allowing parents to find licensed childcare providers based on location, age groups, availability, and specific care requirements. Developed advanced filtering options aligned with government regulations and quality standards. Created detailed provider profile pages showcasing credentials, services, availability, and parent reviews. The platform ensures accessibility and transparency, helping families make informed decisions about childcare while maintaining compliance with state and federal requirements.",
      impact: "Improved provider discoverability • Enhanced parent decision-making • Regulatory compliance • Increased access to quality childcare",
      tech: ["Vue", "React", "Quasar", "Java", "Spring Boot", "Oracle", "Geolocation", "Search Algorithms", "Government APIs", "Accessibility (WCAG)"],
      productUrl: "https://childcareprovidersearch.dhs.hawaii.gov/",
      screenshot: "/images/projects/hawaii-dhs-search-form.png",
      order: 7,
      featured: true,
      createdAt: "2025-01-01T00:00:00.000Z"
    },
    {
      id: "8",
      title: "JIRA Analytics Dashboard",
      description:
        "Advanced project dashboard and analytics suite for JIRA Cloud providing comprehensive reporting, velocity tracking, and team performance insights. Built interactive data visualizations using D3.js and React to help teams understand project health, sprint progress, and resource allocation. Created custom reporting capabilities enabling stakeholders to track KPIs, identify bottlenecks, and make data-driven decisions. The platform transforms raw JIRA data into actionable insights, improving team productivity and project visibility across organizations.",
      impact: "Improved project visibility • Data-driven decision making • Enhanced team productivity • Better resource planning",
      tech: ["React", "D3.js", "Atlassian Connect", "JIRA APIs", "Data Visualization", "Analytics", "TypeScript"],
      productUrl: "https://marketplace.atlassian.com/apps/1220925/custom-charts-for-jira-reports-dashboards-graphs-tables",
      screenshot: "/images/projects/atlassian-custom-charts.png",
      order: 8,
      featured: true,
      createdAt: "2025-01-01T00:00:00.000Z"
    },
    {
      id: "9",
      title: "Sports Betting Tracking Platform",
      description:
        "Comprehensive web-based sports betting tracking and viewing platform designed to personalize the sports watching experience. Developed the complete web application for beta release, featuring a live scoreboard with one-tap access to game streams, favorites system for quick access to teams and players, intelligent Watch Rank algorithm to identify the most exciting games, bet upload functionality for tracking bets from any platform, and real-time stake monitoring with live progress bars. The platform integrates multiple streaming services and provides seamless AirPlay support for TV viewing. Built as a unified sports remote that eliminates the need to juggle multiple score apps, enabling users to track their action and stream every game from a single interface.",
      impact: "Streamlined sports viewing experience • Unified bet tracking • Real-time game intelligence • Enhanced user engagement",
      tech: ["React", "TypeScript", "Real-time Data", "Streaming APIs", "WebRTC", "Responsive Design", "Progressive Web App"],
      productUrl: "https://betcaddy.tv",
      screenshot: "/images/projects/betcaddy-hero.png",
      order: 9,
      featured: true,
      createdAt: "2025-01-01T00:00:00.000Z"
    }
  ],

  techStack: {
    frontend: [
      "React",
      "Next.js",
      "Vue",
      "TailwindCSS",
      "ShadCN",
      "Redux",
      "Zustand",
      "React-Query",
      "Storybook",
      "D3.js",
      "Canvas",
      "Plotly.js",
      "Three.js",
      "Electron",
      "WebSockets",
      "WCAG",
      "i18n",
      "CSS Grid/Flexbox",
      "HTML5",
      "SASS"
    ],
    backend: [
      "Node.js",
      "Express.js",
      "Nest.js",
      "FastAPI",
      "Flask",
      "GraphQL",
      "REST",
      "gRPC",
      "Redis",
      "PostgreSQL",
      "MongoDB"
    ],
    cloud: [
      "AWS",
      "GCP",
      "Azure",
      "Docker",
      "Kubernetes",
      "Terraform",
      "Pulumi",
      "CI/CD (GitHub Actions)",
      "Jenkins",
      "Grafana",
      "Sentry",
      "Datadog",
      "Serverless",
      "Vercel",
      "Netlify"
    ],
    testing: [
      "Jest",
      "Cypress",
      "Playwright",
      "Cucumber",
      "Podman",
      "Mocha",
      "Chai",
      "Enzyme"
    ],
    ai: [
      "Cursor",
      "Windsurf",
      "Claude Code",
      "GPT-4",
      "Claude-Sonnet"
    ],
    tools: [
      "Figma",
      "Adobe XD",
      "Zeplin",
      "Postman",
      "Swagger",
      "Git",
      "GitHub",
      "Bitbucket",
      "GitLab",
      "Vim"
    ]
  },

  lookingFor: {
    title: "What I'm Looking For",
    description:
      "Frontend leadership roles at product-focused companies where I can own the entire user experience. Particularly interested in logistics/TMS, vertical SaaS, or AI-powered products that require complex data visualization and real-time interfaces. Seeking Senior/Staff Frontend Engineer or Frontend Tech Lead positions with high impact and ownership."
  }
};
